import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";

export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Muted line under the title (design `edit.sub`). */
  subtitle?: ReactNode;
  /** Replaces the plain title block (design: initials avatar beside the student's name). */
  titleAdornment?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Panel width in px; 460 for score edits, 480 for the student record (design). */
  width?: number;
}

/**
 * Right-anchored detail panel (design `hasEdit` / `hasDrawer`): full height, a header with the
 * 22 px title and a round outlined close button, a scrollable body and an optional footer.
 */
export const SideDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  titleAdornment,
  children,
  footer,
  width = 460,
}: SideDrawerProps) => {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-labelledby": titleId,
        sx: (theme) => ({
          width,
          maxWidth: "100vw",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: `-20px 0 50px -20px ${theme.palette.tokens.strong}66`,
        }),
      }}
      sx={{
        "& .MuiDrawer-paper": {
          "@media (max-width:479.95px)": { width: "100vw" },
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          px: 3,
          py: 2.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}
        >
          {titleAdornment}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography id={titleId} variant="h3" component="h2">
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: "text.secondary" }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <IconButton
          aria-label={t("common.close")}
          onClick={onClose}
          sx={(theme) => ({
            width: 40,
            height: 40,
            flex: "none",
            border: `1.5px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontSize: 16,
            fontWeight: 700,
          })}
        >
          <span aria-hidden="true">✕</span>
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.75 }}>{children}</Box>
      {footer ? (
        <Box
          sx={(theme) => ({
            px: 3,
            py: 2.25,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          {footer}
        </Box>
      ) : null}
    </Drawer>
  );
};

export default SideDrawer;
